import { useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase, TIER_RANK, type Profile } from '@/lib/supabase'
import { AuthContext, type AuthValue } from '@/auth/context'



const PROFILE_SELECT = `
  id, org_id, role, full_name, email, is_active, auth_method,
  organisations ( id, name, slug, tier, purchaser_type, subscription_status, renews_at, grace_ends_at, data_agreement_signed_at )
`

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)

  async function loadProfile(userId: string) {
    const { data, error } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT)
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Could not load profile', error)
      setProfile(null)
      return
    }
    setProfile(data as unknown as Profile)
  }

  useEffect(() => {
    let active = true

    supabase.auth.getSession().then(async ({ data }) => {
      if (!active) return
      setSession(data.session)
      if (data.session?.user) await loadProfile(data.session.user.id)
      setLoading(false)
    })

    const { data: subscription } = supabase.auth.onAuthStateChange(async (_event, next) => {
      setSession(next)
      if (next?.user) {
        await loadProfile(next.user.id)
      } else {
        setProfile(null)
      }
      setLoading(false)
    })

    return () => {
      active = false
      subscription.subscription.unsubscribe()
    }
  }, [])

  const value = useMemo<AuthValue>(() => {
    const org = profile?.organisations ?? null
    const isKtm = profile?.role === 'ktm_admin' || profile?.role === 'ktm_staff'
    const hasPortalAccess = Boolean(
      profile?.is_active &&
        (isKtm || (org && ['active', 'grace'].includes(org.subscription_status))),
    )

    return {
      session,
      profile,
      loading,
      isKtm: Boolean(isKtm && profile?.is_active),
      isKtmAdmin: Boolean(profile?.role === 'ktm_admin' && profile.is_active),
      isOrgAdmin: Boolean(profile?.role === 'org_admin' && profile.is_active),
      hasPortalAccess,
      tier: org?.tier ?? null,
      meetsTier: (required) => {
        if (isKtm) return true
        if (!org) return false
        return TIER_RANK[org.tier] >= TIER_RANK[required]
      },
      signOut: async () => {
        await supabase.auth.signOut()
        setProfile(null)
      },
      refreshProfile: async () => {
        if (session?.user) await loadProfile(session.user.id)
      },
    }
  }, [session, profile, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
